import { Link, router, usePage } from "@inertiajs/react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Megaphone,
  X,
} from "lucide-react"
import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { csrfToken } from "@/lib/web-push"
import { memberAnnouncementSeenPath, memberPagePath } from "@/routes"
import type { AnnouncementStory, SharedData } from "@/types"

const SWIPE_THRESHOLD_PX = 50
// Pages where a floating card would cover a player or repeat the full list.
const DOCK_HIDDEN_PATHS =
  /^\/app\/(announcements$|replays\/[^/]+|live\/|mudra-sequences\/[^/]+\/practice|programs\/[^/]+\/video\/)/
const COLLAPSED_KEY = "triyoga:announcements:collapsed"

// Announcements that were current when the member collapsed the dock on this
// device. The dock stays collapsed across pages until a new one goes live.
// Falls back to memory when storage is unavailable.
let collapsedFallback = ""
const collapsedListeners = new Set<() => void>()

function readCollapsed() {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) ?? collapsedFallback
  } catch {
    return collapsedFallback
  }
}

function subscribeCollapsed(listener: () => void) {
  collapsedListeners.add(listener)
  window.addEventListener("storage", listener)
  return () => {
    collapsedListeners.delete(listener)
    window.removeEventListener("storage", listener)
  }
}

function writeCollapsed(ids: number[]) {
  collapsedFallback = JSON.stringify(ids)
  try {
    window.localStorage.setItem(COLLAPSED_KEY, collapsedFallback)
  } catch {
    // Private browsing: the in-memory fallback keeps it for this visit.
  }
  collapsedListeners.forEach((listener) => listener())
}

function parseIds(raw: string) {
  try {
    const ids: unknown = JSON.parse(raw)
    return new Set(
      Array.isArray(ids)
        ? ids.filter((id): id is number => typeof id === "number")
        : [],
    )
  } catch {
    return new Set<number>()
  }
}

interface AnnouncementsContextValue {
  stories: AnnouncementStory[]
  unseen: AnnouncementStory[]
  expanded: boolean
  open: (index: number) => void
  collapse: () => void
  expand: () => void
}

const AnnouncementsContext = createContext<AnnouncementsContextValue | null>(
  null,
)

/**
 * Holds the current announcements for the member layout and the full-screen
 * viewer that the floating dock opens.
 */
export function AnnouncementsProvider({ children }: { children: ReactNode }) {
  const stories = usePage<SharedData>().props.announcement_stories ?? []
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [seenHere, setSeenHere] = useState(() => new Set<number>())
  // "Seen" requests sent since the viewer opened; the sidebar count and the
  // shared stories are refreshed once they finish.
  const pendingSeen = useRef<Promise<unknown>[]>([])
  const collapsedRaw = useSyncExternalStore(
    subscribeCollapsed,
    readCollapsed,
    () => "",
  )
  const collapsedIds = useMemo(() => parseIds(collapsedRaw), [collapsedRaw])

  const unseen = stories.filter(
    (story) => !story.seen && !seenHere.has(story.id),
  )
  const expanded = stories.some((story) => !collapsedIds.has(story.id))
  const collapse = () => writeCollapsed(stories.map((story) => story.id))
  const expand = () => writeCollapsed([])

  const show = (index: number) => {
    setOpenIndex(index)
    const story = stories[index]
    if (!story || story.seen || seenHere.has(story.id)) return

    setSeenHere((current) => new Set(current).add(story.id))
    pendingSeen.current.push(
      fetch(memberAnnouncementSeenPath(story.id), {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
      }),
    )
  }

  const close = () => {
    setOpenIndex(null)
    // Once everything has been seen the dock steps back to its small form.
    if (unseen.length === 0) collapse()
    const pending = pendingSeen.current
    pendingSeen.current = []
    if (pending.length > 0) {
      void Promise.allSettled(pending).then(() =>
        router.reload({
          only: ["announcements_count", "announcement_stories"],
        }),
      )
    }
  }

  return (
    <AnnouncementsContext.Provider
      value={{ stories, unseen, expanded, open: show, collapse, expand }}
    >
      {children}
      {stories.length > 0 && (
        <AnnouncementViewer
          stories={stories}
          index={openIndex}
          onIndexChange={show}
          onClose={close}
        />
      )}
    </AnnouncementsContext.Provider>
  )
}

function StoryThumb({
  story,
  className,
}: {
  story: AnnouncementStory
  className?: string
}) {
  return story.image_url ? (
    <img
      src={story.image_url}
      alt=""
      className={cn("bg-muted object-cover", className)}
    />
  ) : (
    <span
      className={cn(
        "bg-muted flex items-center justify-center text-sm font-medium",
        className,
      )}
    >
      {story.title.charAt(0)}
    </span>
  )
}

function NewBadge({ count }: { count: number }) {
  return (
    <span className="rounded-full bg-[#ff3b30] px-2 py-0.5 text-xs leading-none font-medium text-white">
      {count} new
    </span>
  )
}

/**
 * Announcements float over every member page: a large card in the
 * bottom-right corner on desktop and a bar above the tab bar on phones. Both
 * collapse to a smaller chip or bubble, and sit behind dialogs, sheets and
 * video overlays.
 */
export function AnnouncementDock() {
  const announcements = useContext(AnnouncementsContext)
  const { url } = usePage()

  if (!announcements || announcements.stories.length === 0) return null
  if (DOCK_HIDDEN_PATHS.test(url.split(/[?#]/, 1)[0] ?? "")) return null

  const { stories, unseen, expanded, open, collapse, expand } = announcements
  // The admin's order decides which slide leads, whether or not it's new.
  const featured = stories[0]
  const details = [
    stories.length > 1 && `1 of ${stories.length}`,
    featured.ends_label,
  ]
    .filter(Boolean)
    .join(" · ")
  const openLabel = `Open announcement: ${featured.title}`

  return (
    <div className="print:hidden">
      <div className="fixed right-6 bottom-6 z-30 hidden md:block">
        {expanded ? (
          <div className="animate-in fade-in-0 slide-in-from-bottom-4 relative w-80">
            {stories.length > 1 && (
              <>
                <span
                  aria-hidden="true"
                  className="bg-card absolute inset-0 translate-x-3 -translate-y-3 rounded-2xl border opacity-40"
                />
                <span
                  aria-hidden="true"
                  className="bg-card absolute inset-0 translate-x-1.5 -translate-y-1.5 rounded-2xl border opacity-70"
                />
              </>
            )}
            <div className="bg-card relative overflow-hidden rounded-2xl border shadow-2xl">
              <div className="flex items-center gap-2 px-4 pt-3 pb-2.5">
                <Megaphone className="text-muted-foreground size-4" />
                <span className="text-sm font-medium">Announcements</span>
                {unseen.length > 0 && <NewBadge count={unseen.length} />}
                <button
                  type="button"
                  onClick={collapse}
                  className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -mr-1.5 ml-auto rounded-full p-1.5 focus-visible:ring-2 focus-visible:outline-none"
                  aria-label="Collapse announcements"
                >
                  <ChevronDown className="size-4" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => open(0)}
                className="group focus-visible:ring-ring block w-full text-left focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                aria-label={openLabel}
              >
                <span className="mx-3 block aspect-[7/5] overflow-hidden rounded-xl">
                  <StoryThumb
                    story={featured}
                    className="size-full transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                </span>
                <span className="flex items-center gap-3 px-4 pt-3 pb-4">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {featured.title}
                    </span>
                    {details && (
                      <span className="text-muted-foreground block truncate text-xs">
                        {details}
                      </span>
                    )}
                  </span>
                  <span className="bg-primary text-primary-foreground group-hover:bg-primary/90 shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors">
                    View
                  </span>
                </span>
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={expand}
            className="bg-card hover:bg-accent animate-in fade-in-0 slide-in-from-bottom-2 focus-visible:ring-ring flex items-center gap-3 rounded-2xl border p-2 pr-4 shadow-xl transition-colors focus-visible:ring-2 focus-visible:outline-none"
            aria-label={
              unseen.length > 0
                ? `Show announcements, ${unseen.length} new`
                : "Show announcements"
            }
          >
            <StoryThumb story={featured} className="h-11 w-16 rounded-lg" />
            <span className="text-left">
              <span className="block text-sm font-medium">Announcements</span>
              <span className="text-muted-foreground block text-xs">
                {unseen.length > 0
                  ? `${unseen.length} new`
                  : `${stories.length} current`}
              </span>
            </span>
            {unseen.length > 0 && (
              <span
                aria-hidden="true"
                className="size-2.5 rounded-full bg-[#ff3b30]"
              />
            )}
            <ChevronUp className="text-muted-foreground size-4" />
          </button>
        )}
      </div>

      {/* Phones: docked just above the tab bar. */}
      <div className="pointer-events-none fixed inset-x-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom,0px))] z-30 md:hidden">
        {expanded ? (
          <div
            data-announcement-bar
            className="bg-background/85 animate-in fade-in-0 slide-in-from-bottom-2 pointer-events-auto flex items-center gap-1 rounded-2xl border p-2 shadow-xl backdrop-blur-xl"
          >
            <button
              type="button"
              onClick={() => open(0)}
              className="focus-visible:ring-ring flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left focus-visible:ring-2 focus-visible:outline-none"
              aria-label={openLabel}
            >
              <StoryThumb
                story={featured}
                className="h-14 w-20 shrink-0 rounded-xl"
              />
              <span className="min-w-0 flex-1">
                <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  <Megaphone className="size-3.5" />
                  Announcements
                  {unseen.length > 0 && <NewBadge count={unseen.length} />}
                </span>
                <span className="mt-0.5 block truncate text-sm font-medium">
                  {featured.title}
                </span>
                {details && (
                  <span className="text-muted-foreground block truncate text-xs">
                    {details}
                  </span>
                )}
              </span>
            </button>
            <button
              type="button"
              onClick={collapse}
              className="text-muted-foreground hover:bg-accent focus-visible:ring-ring shrink-0 rounded-full p-2 focus-visible:ring-2 focus-visible:outline-none"
              aria-label="Collapse announcements"
            >
              <ChevronDown className="size-5" />
            </button>
          </div>
        ) : (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={expand}
              className="animate-in fade-in-0 zoom-in-90 focus-visible:ring-ring pointer-events-auto relative rounded-full shadow-xl focus-visible:ring-2 focus-visible:outline-none"
              aria-label={
                unseen.length > 0
                  ? `Show announcements, ${unseen.length} new`
                  : "Show announcements"
              }
            >
              <span
                className={cn(
                  "block rounded-full p-[3px]",
                  unseen.length > 0
                    ? "bg-gradient-to-tr from-amber-400 via-orange-500 to-rose-500"
                    : "bg-border",
                )}
              >
                <span className="bg-background block rounded-full p-[2px]">
                  <StoryThumb
                    story={featured}
                    className="size-12 rounded-full"
                  />
                </span>
              </span>
              {unseen.length > 0 && (
                <span className="border-background absolute -top-1 -right-1 flex min-w-5 items-center justify-center rounded-full border-2 bg-[#ff3b30] px-1 text-[11px] leading-4 font-medium text-white">
                  {unseen.length}
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function AnnouncementViewer({
  stories,
  index,
  onIndexChange,
  onClose,
}: {
  stories: AnnouncementStory[]
  index: number | null
  onIndexChange: (index: number) => void
  onClose: () => void
}) {
  const touchStartX = useRef<number | null>(null)
  const story = index === null ? null : stories[index]
  const hasPrevious = index !== null && index > 0
  const hasNext = index !== null && index < stories.length - 1

  const previous = () => {
    if (index !== null && hasPrevious) onIndexChange(index - 1)
  }
  const next = () => {
    if (index !== null && hasNext) onIndexChange(index + 1)
    else onClose()
  }

  return (
    <DialogPrimitive.Root
      open={story !== null}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/95" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex flex-col text-white outline-none"
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") previous()
            if (event.key === "ArrowRight") next()
          }}
          onTouchStart={(event) => {
            touchStartX.current = event.touches[0]?.clientX ?? null
          }}
          onTouchEnd={(event) => {
            const start = touchStartX.current
            touchStartX.current = null
            const end = event.changedTouches[0]?.clientX
            if (start === null || end === undefined) return
            if (end - start > SWIPE_THRESHOLD_PX) previous()
            if (start - end > SWIPE_THRESHOLD_PX) next()
          }}
          aria-describedby={undefined}
        >
          {story && index !== null && (
            <>
              <div className="mx-auto w-full max-w-5xl space-y-3 px-4 pt-[max(1rem,env(safe-area-inset-top))]">
                {stories.length > 1 && (
                  <div className="flex gap-1" aria-hidden="true">
                    {stories.map((item, itemIndex) => (
                      <span
                        key={item.id}
                        className={cn(
                          "h-0.5 flex-1 rounded-full",
                          itemIndex <= index ? "bg-white" : "bg-white/30",
                        )}
                      />
                    ))}
                  </div>
                )}
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <DialogPrimitive.Title className="truncate text-base font-medium">
                      {story.title}
                    </DialogPrimitive.Title>
                    <p className="text-sm text-white/70">
                      {stories.length > 1 &&
                        `${index + 1} of ${stories.length}`}
                      {stories.length > 1 && story.ends_label && " · "}
                      {story.ends_label}
                    </p>
                  </div>
                  <DialogPrimitive.Close
                    className="-m-2 rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
                    aria-label="Close announcements"
                  >
                    <X className="size-6" />
                  </DialogPrimitive.Close>
                </div>
              </div>

              {/* Tapping the left third goes back, anywhere else goes forward. */}
              <div
                className="relative flex min-h-0 flex-1 items-center justify-center px-4 py-4 md:px-20"
                onClick={(event) => {
                  const bounds = event.currentTarget.getBoundingClientRect()
                  if (event.clientX - bounds.left < bounds.width / 3) previous()
                  else next()
                }}
              >
                {story.image_url ? (
                  <img
                    key={story.id}
                    src={story.image_url}
                    alt={story.title}
                    className="animate-in fade-in-0 max-h-full max-w-full rounded-lg object-contain duration-200 select-none"
                    draggable={false}
                  />
                ) : (
                  <div className="max-w-xl rounded-lg bg-white/10 p-8 text-center">
                    <p className="text-2xl font-medium">{story.title}</p>
                    {story.excerpt && (
                      <p className="mt-4 text-white/80">{story.excerpt}</p>
                    )}
                  </div>
                )}
                {hasPrevious && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      previous()
                    }}
                    className="absolute top-1/2 left-4 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none md:block"
                    aria-label="Previous announcement"
                  >
                    <ChevronLeft className="size-6" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    next()
                  }}
                  className="absolute top-1/2 right-4 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none md:block"
                  aria-label={
                    hasNext ? "Next announcement" : "Close announcements"
                  }
                >
                  {hasNext ? (
                    <ChevronRight className="size-6" />
                  ) : (
                    <X className="size-6" />
                  )}
                </button>
              </div>

              <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-3 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] text-center">
                {story.image_url && story.excerpt && (
                  <p className="max-w-xl text-sm text-white/80">
                    {story.excerpt}
                  </p>
                )}
                <div className="flex items-center gap-3">
                  {story.has_content && (
                    <Button
                      asChild
                      variant="secondary"
                      className="bg-white text-black hover:bg-white/90"
                    >
                      <Link href={memberPagePath(story.slug)}>Read more</Link>
                    </Button>
                  )}
                  {stories.length > 1 && (
                    <p className="text-xs text-white/60 md:hidden">
                      Tap or swipe for more
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
